import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Skeleton,
} from '@clouway/ui'

import { ApiError } from '@/api'
import { useProductCatalog } from '@/hooks/useProductCatalog'
import { isUncapped, scopeGroupsForProduct, toggleScope } from './allowedScopes/transform'

// The mint-ceiling editor for a service account or OAuth client's
// allowed_scopes — shared by both features' edit screens (they differ only in
// where the current ceiling and the save mutation come from, both passed in
// as props). CRITICAL: an empty selection is not "deny everything" — it's the
// uncapped state (see AllowedScopesBase.kt) — so the copy and the confirm
// step below both treat "save with nothing checked" as an explicit, safe
// choice rather than a destructive one.
export function AllowedScopesEditor({
  allowedScopeIds,
  isLoading,
  onSave,
  readOnly = false,
}: {
  allowedScopeIds: string[] | undefined
  isLoading: boolean
  // Returns whatever the underlying mutation resolves to (the saved ceiling,
  // typically) — the editor only cares that it resolved, not with what.
  onSave: (scopeIds: string[]) => Promise<unknown>
  readOnly?: boolean
}) {
  const { t, i18n } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { products, structuresByProduct, isLoading: catalogLoading } = useProductCatalog()
  const [selected, setSelected] = useState<Set<string>>(() => new Set(allowedScopeIds ?? []))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Transient "saved" confirmation on the button itself — there's no toast
  // infrastructure in this app, so a self-clearing local state is the
  // lightest way to confirm the write actually happened. Any further edit
  // invalidates it (see the checkbox handler below) since it no longer
  // describes the current selection.
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!saved) return
    const handle = setTimeout(() => setSaved(false), 2000)
    return () => clearTimeout(handle)
  }, [saved])

  // Resyncs from the server value whenever it changes (initial load, or after
  // a successful save refetches it) — mirrors AccessTab's product auto-select
  // effect rather than deriving state once at mount, since allowedScopeIds is
  // undefined until its own query resolves. Keyed on a joined string rather
  // than the array itself: both edit screens pass a freshly-mapped array
  // (`allowedScopes?.map(s => s.id)`) on every render, so keying on
  // referential identity would re-fire this effect — and clobber whatever
  // the admin had checked but not yet saved — on any unrelated parent
  // re-render, not just when the ceiling actually changes.
  const allowedScopeIdsKey = allowedScopeIds?.join(',')
  useEffect(() => {
    if (allowedScopeIds) {
      setSelected(new Set(allowedScopeIds))
    }
    // allowedScopeIdsKey is the intended dependency (see comment above);
    // allowedScopeIds itself is derived from it each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedScopeIdsKey])

  const save = async (scopeIds: string[]) => {
    setError(null)
    setSaved(false)
    setSubmitting(true)
    try {
      await onSave(scopeIds)
      setSaved(true)
    } catch (caught) {
      // A source='yaml' OAuth client refuses the write with 409 — that's a
      // legitimate ownership response, not a bug, so it gets its own copy
      // rather than the generic failure message.
      setError(caught instanceof ApiError && caught.status === 409 ? t('allowedScopes.conflict') : t('allowedScopes.saveError'))
    } finally {
      setSubmitting(false)
    }
  }

  // Any further checkbox change makes the "saved" confirmation stale — it
  // was a snapshot of a selection the admin has since moved away from.
  const toggle = (id: string) => {
    setSelected((prev) => toggleScope(prev, id))
    setSaved(false)
  }

  if (isLoading || catalogLoading) {
    return <Skeleton className="h-48 w-full" />
  }

  const uncapped = isUncapped(selected)
  const saveLabel = submitting ? t('allowedScopes.saving') : saved ? t('allowedScopes.saved') : t('allowedScopes.save')

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{t('allowedScopes.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-muted-foreground text-xs">{t(uncapped ? 'allowedScopes.uncapped' : 'allowedScopes.capped')}</p>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {products.length === 0 ? (
          <span className="text-muted-foreground text-sm">{t('allowedScopes.empty')}</span>
        ) : (
          products.map((product) => {
            const structure = structuresByProduct[product]
            if (!structure) return null
            const groups = scopeGroupsForProduct(structure, i18n.language)
            return (
              <div key={product} className="flex flex-col gap-2">
                <div className="text-sm font-medium">{product}</div>
                {groups.map(
                  (group) =>
                    group.options.length > 0 && (
                      <div key={group.domain} className="flex flex-col gap-1 pl-2">
                        <div className="text-muted-foreground text-xs font-medium">{group.domainLabel}</div>
                        <div className="flex flex-wrap gap-3">
                          {group.options.map((option) => (
                            <label key={option.id} className="flex items-center gap-1 text-xs">
                              <Checkbox
                                checked={selected.has(option.id)}
                                disabled={readOnly}
                                onCheckedChange={() => toggle(option.id)}
                              />
                              {option.resourceLabel} · {t(`allowedScopes.${option.kind}`)}
                            </label>
                          ))}
                        </div>
                      </div>
                    ),
                )}
              </div>
            )
          })
        )}
      </CardContent>
      {!readOnly && (
        <CardFooter className="flex justify-end">
          {uncapped ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" disabled={submitting}>
                  {saved && <Check />}
                  {saveLabel}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('allowedScopes.confirmEmpty.title')}</AlertDialogTitle>
                  <AlertDialogDescription>{t('allowedScopes.confirmEmpty.description')}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => save([])}>{t('allowedScopes.confirmEmpty.confirm')}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button type="button" disabled={submitting} onClick={() => save([...selected])}>
              {saved && <Check />}
              {saveLabel}
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  )
}
